import { AsyncPipe, DecimalPipe, NgStyle } from '@angular/common';
import {
  computed,
  Component,
  effect,
  inject,
  linkedSignal,
  untracked,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ForecastSeries } from '@api-response-types/forecast-series.response';
import { AggregationLevel } from '@api-types/enums/aggregation-level.enum';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { Measure } from '@api-types/enums/measure.enum';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  FormInputSelectionDirective,
  H2Component,
  PmButtonComponent,
  PmIconComponent,
  PmMenuComponent,
  PmTabsGroupComponent,
  PmTooltipDirective,
  SelectTabOption,
} from '@libs/ui';
import { selectFeatureFlagById } from '@core/store/feature-flags/feature-flags.selectors';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { ErrorMeasureStateService } from 'demand-forecasting/store/error-measure/error-measure-state.service';
import { selectAvailableFilters } from 'demand-forecasting/store/filter/filter.selectors';
import {
  createMeasureVisibility,
  getMeasureColumnHeader,
} from 'demand-forecasting/analysis/services/measure-display';
import { ForecastSeriesActions } from 'demand-forecasting/store/forecast-series/forecast-series.actions';
import {
  isForecastSeriesFetching,
  latestCompletedForecastSeries,
  selectFutureErrorMeasure,
  selectFutureErrorMeasureValue,
  selectIsFutureErrorMeasureCalculating,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';

import { LucideInfo, LucideSettings2 } from '@lucide/angular';

const MEASURES_WITHOUT_ACCURACY: ReadonlySet<Measure> = new Set([
  Measure.Mae,
  Measure.Rmse,
  Measure.Me,
  Measure.Wpe,
  Measure.Mpe,
]);

@Component({
  selector: 'pm-forecast-kpis',
  templateUrl: './forecast-kpis.component.html',
  imports: [
    MatMenuTrigger,
    ReactiveFormsModule,
    FormsModule,
    MatProgressSpinner,
    PmTooltipDirective,
    NgStyle,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
    H2Component,
    PmButtonComponent,
    PmIconComponent,
    PmMenuComponent,
    PmTabsGroupComponent,
    FormFieldComponent,
    FormInputSelectComponent,
    FormInputSelectionDirective,
  ],
})
export class ForecastKpisComponent {
  private readonly store = inject(Store);
  protected translateService = inject(TranslateService);
  private readonly colorService = inject(ChartColorService);
  private readonly errorMeasureService = inject(ErrorMeasureStateService);

  protected readonly settingsIcon = LucideSettings2;
  protected readonly infoIcon = LucideInfo;

  protected readonly errorMeasureFeatureFlag = this.store.selectSignal(
    selectFeatureFlagById(FeatureFlagId.error_measure),
  );

  protected readonly futureErrorMeasureFeatureFlag = this.store.selectSignal(
    selectFeatureFlagById(FeatureFlagId.future_error_measure),
  );

  private readonly isMeasureVisible = createMeasureVisibility(this.store);

  color = this.colorService.getColors('aggregated')[0];

  errorMeasureHttpResource = this.errorMeasureService.errorMeasure;

  appliedMeasureOptionsEstErr =
    this.errorMeasureService.selectedErrorMeasureOptions;

  isCalculatingFutureMeasure = this.store.selectSignal(
    selectIsFutureErrorMeasureCalculating,
  );

  latestForecastSeries$ = this.store.select(latestCompletedForecastSeries);
  isForecastSeriesCalculating = this.store.selectSignal(
    isForecastSeriesFetching,
  );

  configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );
  selectedFutureErrorMeasureValue = this.store.selectSignal(
    selectFutureErrorMeasureValue,
  );

  selectedMeasure = linkedSignal<Measure>(
    () => this.appliedMeasureOptionsEstErr().measureType,
  );

  appliedMeasureForFutureErrorMeasure = this.store.selectSignal(
    selectFutureErrorMeasure,
  );

  protected readonly isEstimatedErrorMeasureCalculating = computed(
    () =>
      this.errorMeasureHttpResource.isLoading() ||
      this.errorMeasureService.isPreferenceLoading() ||
      this.isForecastSeriesCalculating(),
  );

  protected readonly estimatedErrorMeasure = computed<number | null>(() =>
    this.errorMeasureHttpResource.hasValue()
      ? (this.errorMeasureHttpResource.value() ?? null)
      : null,
  );

  protected readonly showEstimatedAccuracy = computed(() =>
    this.hasAccuracy(this.appliedMeasureOptionsEstErr().measureType),
  );

  protected readonly showFutureAccuracy = computed(() =>
    this.hasAccuracy(this.appliedMeasureForFutureErrorMeasure()),
  );

  protected readonly aggregationLevelTabs: SelectTabOption[] = [
    {
      value: AggregationLevel.HIGHEST,
      label: 'RESULT.KPIS.AGGREGATION.HIGHEST',
      disabled: false,
    },
    {
      value: AggregationLevel.CUSTOM,
      label: 'RESULT.KPIS.AGGREGATION.CUSTOM',
      disabled: false,
    },
  ];

  protected readonly aggregationLevel = linkedSignal<AggregationLevel>(() =>
    this.appliedMeasureOptionsEstErr().measureGroupColumns.length > 0
      ? AggregationLevel.CUSTOM
      : AggregationLevel.HIGHEST,
  );
  protected readonly selectedAggregationLevels = linkedSignal<string[]>(
    () => this.appliedMeasureOptionsEstErr().measureGroupColumns,
  );

  protected readonly isCustomAggregation = computed(
    () => this.aggregationLevel() === AggregationLevel.CUSTOM,
  );

  private readonly availableFilters = this.store.selectSignal(
    selectAvailableFilters,
  );

  protected readonly aggregationLevelOptions = computed(() =>
    (this.availableFilters() ?? [])
      .filter((filter) => !!filter.column)
      .map((filter) => ({ value: filter.column, label: filter.column })),
  );

  protected readonly isAnyMeasureCalculating = computed(
    () =>
      this.isEstimatedErrorMeasureCalculating() ||
      this.isCalculatingFutureMeasure(),
  );

  protected readonly isCalculateDisabled = computed(
    () =>
      this.isAnyMeasureCalculating() ||
      (this.isCustomAggregation() &&
        this.selectedAggregationLevels().length === 0),
  );

  constructor() {
    effect(() => {
      const options = this.measureOptions();
      const selected = this.selectedMeasure();
      if (options.length > 0 && !options.some((o) => o.value === selected)) {
        this.selectedMeasure.set(options[0].value);
        this.syncMeasureOptions();
      }
    });

    effect(() => {
      if (this.errorMeasureService.isPreferenceLoading()) {
        return;
      }
      untracked(() => this.applyFutureErrorMeasure());
    });
  }

  onMeasureChange(measure: Measure): void {
    this.selectedMeasure.set(measure);
    this.applyMeasure(this.appliedMeasureOptionsEstErr().measureGroupColumns);
  }

  onAggregationLevelChange(level: string): void {
    this.aggregationLevel.set(level as AggregationLevel);
    if (level === AggregationLevel.HIGHEST) {
      this.selectedAggregationLevels.set([]);
    }
  }

  onCalculate(): void {
    this.applyMeasure(
      this.isCustomAggregation() ? this.selectedAggregationLevels() : [],
    );
  }

  accuracyOf(errorMeasureValue: number): number {
    return (1 - errorMeasureValue) * 100;
  }

  getNameOfForecastSeries(forecastSeries: ForecastSeries) {
    if (forecastSeries._resultSetStatus === ForecastVersionStatus.FINISHED) {
      return forecastSeries.name;
    }
    return '';
  }

  private syncMeasureOptions(): void {
    this.errorMeasureService.selectedErrorMeasureOptions.set({
      measureType: this.selectedMeasure(),
      measureGroupColumns:
        this.appliedMeasureOptionsEstErr().measureGroupColumns,
    });
    this.applyFutureErrorMeasure();
  }

  private applyMeasure(measureGroupColumns: string[]): void {
    this.errorMeasureService.applyErrorMeasureOptions({
      measureType: this.selectedMeasure(),
      measureGroupColumns,
    });
    this.applyFutureErrorMeasure();
  }

  private applyFutureErrorMeasure(): void {
    this.store.dispatch(
      ForecastSeriesActions.setSelectedFutureErrorMeasure({
        measureForFutureErrorMeasure: this.selectedMeasure(),
      }),
    );
    this.store.dispatch(
      ForecastSeriesActions.recalculateSelectedFutureErrorMeasureValue(),
    );
  }

  private hasAccuracy(measure: Measure | undefined): boolean {
    if (!measure || !this.configuration()?.startForecastConfig) {
      return false;
    }
    return !MEASURES_WITHOUT_ACCURACY.has(measure);
  }

  protected measureOptions = computed(() =>
    Object.values(Measure)
      .filter((measure) => this.isMeasureVisible(measure))
      .map((measure) => ({
        value: measure,
        label: getMeasureColumnHeader(measure),
        description: `RESULT.MEASURE.EXPLANATION.${measure.toUpperCase()}`,
      })),
  );
}
